import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GroupsService } from '@/api';
import { toast } from 'sonner';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { errorToastText } from '@/lib/translateBackendError';
import type { 
  CreateGroupRequest, 
  UpdateGroupRequest 
} from '@/types';

// Query keys
export const groupsKeys = {
  all: ['groups'] as const,
  lists: () => [...groupsKeys.all, 'list'] as const,
  list: (orgId: string) => [...groupsKeys.lists(), orgId] as const,
  details: () => [...groupsKeys.all, 'detail'] as const,
  detail: (id: string) => [...groupsKeys.details(), id] as const,
};

// List groups
export function useGroups(orgId: string, limit = 100, offset = 0) {
  return useQuery({
    queryKey: groupsKeys.list(orgId),
    queryFn: () => GroupsService.getApiV1Groups(limit, offset),
    select: (data) =>
      [...(data || [])].sort(
        (a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || (a.name ?? '').localeCompare(b.name ?? '')
      ),
    enabled: !!orgId,
  });
}

// Get single group
export function useGroup(groupId: string) {
  return useQuery({
    queryKey: groupsKeys.detail(groupId),
    queryFn: () => GroupsService.getApiV1Groups1(groupId),
    enabled: !!groupId,
  });
}

// Create group
export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGroupRequest) =>
      GroupsService.postApiV1Groups(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.all });
      toast.success(t`Group created successfully`);
    },
    onError: (error) => {
      toast.error(errorToastText(error, i18n, t`Failed to create group`));
    },
  });
}

// Update group
export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateGroupRequest }) =>
      GroupsService.putApiV1Groups(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.all });
      queryClient.invalidateQueries({ queryKey: groupsKeys.detail(variables.id) });
      toast.success(t`Group updated successfully`);
    },
    onError: (error) => {
      toast.error(errorToastText(error, i18n, t`Failed to update group`));
    },
  });
}

// Delete group
export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => GroupsService.deleteApiV1Groups(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupsKeys.all });
      toast.success(t`Group deleted successfully`);
    },
    onError: (error) => {
      toast.error(errorToastText(error, i18n, t`Failed to delete group`));
    },
  });
}